// Signing a player in at a kiosk — `/v1/station/sign-in/*` (spec/07 §4.1, spec/08 §4.1, F-IAM-01).
//
// A whole venue shares one public IP, so a kiosk never calls the public `/v1/auth/*` sign-in
// endpoints: better-auth limits those per IP, and fifty PCs behind one address would throttle the
// floor. Instead the kiosk calls these, which are **station-authenticated** — the caller is the
// station, proved by its kiosk token (spec/07 §1, §5), never a user session — and the server calls
// better-auth itself, applying limits that are keyed on things a venue does not share:
//
//   per station        10 attempts a minute
//   per phone number   3 codes per 10 minutes, 10 a day (spec/07 §2.2)
//   per account        5 failures per 15 minutes, then a 15-minute lock
//
// Phone sign-in is two calls: ask for a code, then sign in with it; password sign-in is one call
// taking an email address or a username (F-IAM-02). No better-auth session is ever
// created on the PC (spec/07 §4.1); what comes back identifies the player, and starting the
// session and issuing the station-scoped player token (§4.3) are added by F-SES-01.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { PlayerIdSchema } from "../ids/schema.js";
import { IsoDateTimeSchema, LocalDateSchema } from "../primitives/scalars.js";
import { SignUpConsentSchema } from "../primitives/consent.js";
import { OtpChannelSchema, OtpCodeSchema } from "../primitives/otp.js";
import { PhoneNumberSchema } from "../primitives/phone-number.js";
import { UsernameSchema } from "../primitives/username.js";
import { commonProblemResponses, jsonResponse, problemResponse } from "../openapi/responses.js";
/**
 * A banned player at a kiosk (spec/07 §3.5, spec/05 §3.2 check 3, F-IAM-07). Checked only once the
 * code or the password has proved who they are, so the refusal says nothing to someone who merely
 * knows a number; and its `detail` is the same sentence for every scope, never the reason.
 */
const BANNED = 'The player is banned here — at this venue, across its organisation, or from the Platform. The detail is ' +
    '"Please see the front desk." whichever it is, and never the reason.';
/** `auth.phoneSignIn` off (spec/07 §3.1, the owner's instruction of 2026-10-08). */
const PHONE_SIGN_IN_OFF = 'Phone sign-in is turned off on the Platform (`auth.phoneSignIn`): nothing is counted, looked up or sent, and the ' +
    'kiosk offers email and password instead.';
const STATION_AUTHENTICATION = 'Station-authenticated: `Authorization: Bearer <kiosk token>` (spec/07 §4.1). A user session is ' +
    'not accepted here, and no better-auth session is created on the station.';
export const StationPhoneCodeRequestSchema = z
    .object({
    phoneNumber: PhoneNumberSchema,
    /**
     * The channel the player picked, when the kiosk offers the choice. A preference only: it is
     * honoured when `auth.otpChannel` allows it and falls back to SMS when it cannot be delivered.
     */
    channel: OtpChannelSchema.optional(),
})
    .openapi('StationPhoneCodeRequest');
export const StationPhoneCodeSentSchema = z
    .object({
    /** The channel the code actually went by. */
    channel: OtpChannelSchema,
    /** How long the code is valid. */
    expiresInSeconds: z.number().int().positive(),
    /** When the kiosk may offer to send another. */
    resendAfterSeconds: z.number().int().positive(),
})
    .openapi('StationPhoneCodeSent', { description: 'A code is on its way to the phone.' });
export const StationPhoneSignInSchema = z
    .object({
    phoneNumber: PhoneNumberSchema,
    code: OtpCodeSchema,
    /**
     * Terms, privacy and marketing, as the player accepted them (F-CMP-06). Required when this code
     * creates the account: a new number without it is answered `consent-required` and **the code
     * stays valid**, so the kiosk shows the consents and sends the same code again with them. Ignored
     * for an account that already exists.
     */
    consent: SignUpConsentSchema.optional(),
    /**
     * The gamer tag the player typed at kiosk create-account step 4 (spec/12 §3.3), sent with the
     * same resend that carries `consent`. Optional — a player never has to choose one to finish
     * creating an account: left out, or already taken by the time this is written, the server
     * assigns a fresh one (spec/07 "Required profile"). Ignored for an account that already exists.
     */
    username: UsernameSchema.optional(),
})
    .openapi('StationPhoneSignIn');
/** The player a sign-in resolved to. An object rather than a bare id so M3 can add to it. */
export const StationSignedInPlayerSchema = z
    .object({ id: PlayerIdSchema })
    .openapi('StationSignedInPlayer', { description: 'The player a station signed in.' });
/**
 * Create-account steps 4b–6 (spec/12 §3.3, F-KIO-04): the right to finish the profile of the account this
 * sign-in has just created, from this station, for a short window. Only present when `createdAccount` is
 * true. The token is opaque, single-player and single-station: `POST /v1/station/sign-in/profile` refuses it
 * from any other station, for any other player, or once `expiresAt` has passed (`profile-window-closed`).
 */
export const StationProfileCompletionGrantSchema = z
    .object({
    token: z.string().min(32).max(128),
    expiresAt: IsoDateTimeSchema,
    /**
     * Whether the player may add an email address here: true for an account made by phone (its address is
     * synthetic), false for one made by email (it already has a verified one).
     */
    canAddEmail: z.boolean(),
})
    .openapi('StationProfileCompletionGrant', {
    description: 'The short window in which a just-created account may finish its profile at this station.',
});
export const StationSignInResultSchema = z
    .object({
    player: StationSignedInPlayerSchema,
    /** True when this sign-in created the account — the kiosk then continues to "create account" step 4 (spec/12 §3.3). */
    createdAccount: z.boolean(),
    /** Present only when `createdAccount` is true. Optional: an older kiosk ignores it. */
    profileCompletion: StationProfileCompletionGrantSchema.optional(),
})
    .openapi('StationSignInResult', { description: 'The player the station has signed in.' });
export const StationPasswordSignInSchema = z
    .object({
    /** An email address or a username (spec/12 §3.2: "Email or username + password"). */
    identifier: z.string().trim().min(1).max(254),
    password: z.string().min(1).max(256),
})
    .openapi('StationPasswordSignIn');
// -------------------------------------------------------------------------------------------------
// Which sign-up paths this kiosk offers (F-KIO-04, 2026-10-01)
// -------------------------------------------------------------------------------------------------
/** The ways a new player can create an account at a kiosk. */
export const STATION_SIGN_UP_METHODS = ['phone', 'email'];
export const StationSignUpOptionsSchema = z
    .object({
    /** The venue's `kiosk.allowSignUp` (default true). False: the kiosk hides "Create account". */
    allowed: z.boolean(),
    /**
     * The paths that can work here, in the order the kiosk offers them: `phone` when a phone-code
     * provider (SMS or WhatsApp) is configured and phone sign-in is on (`auth.phoneSignIn`), `email` when
     * an email provider is. Empty when sign-up is not allowed or neither path can work — then the kiosk
     * hides "Create account" too.
     */
    methods: z.array(z.enum(STATION_SIGN_UP_METHODS)),
})
    .openapi('StationSignUpOptions');
export const StationSignInMethodsSchema = z
    .object({
    /**
     * `auth.phoneSignIn` (spec/07 §3.1): whether a phone and a code sign a player in. Off — the default since
     * 2026-10-08 — the kiosk hides its phone tab and `signUp.methods` never names `phone`.
     */
    phone: z.boolean(),
    /**
     * The sign-in handoff (ADR-0051, F-KIO-20): whether this server can carry a sign-in out to a browser or a phone and
     * back. True on every server that has it; an answer without it (a server from before) is read as false, and the
     * kiosk offers neither "Sign in with your phone" nor a provider button.
     */
    handoff: z.boolean(),
    /**
     * The providers this deployment has switched on (Google, Discord, Steam), offered at the kiosk as buttons that open
     * the station's sign-in browser on the handoff. Names only. Empty when none is configured. A PC with no browser it
     * can lock down (Edge or Chrome) hides them itself.
     */
    providers: z.array(z.enum(['google', 'discord', 'steam'])),
    /**
     * "I have a ticket" (F-SES-06, spec/12 §3.2): whether this venue serves guests (`sessions.guestsEnabled`), so the
     * kiosk offers the ticket code. Absent — a server from before guest passes — is read as no.
     */
    ticket: z.boolean().optional(),
})
    .openapi('StationSignInMethods', { description: 'The ways a player can sign in at this kiosk beyond a password.' });
export const StationSignInOptionsSchema = z
    .object({
    signUp: StationSignUpOptionsSchema,
    signIn: StationSignInMethodsSchema,
    /**
     * Whether create-account asks for a date of birth as required rather than optional: this station's
     * class carries an age limit (spec/05 §3.2 check 5), so a player without one could not start here.
     */
    dateOfBirthRequired: z.boolean(),
})
    .openapi('StationSignInOptions', { description: 'What the kiosk’s sign-in panel offers at this station.' });
// -------------------------------------------------------------------------------------------------
// Email-code sign-in and sign-up (F-KIO-04, 2026-10-01)
// -------------------------------------------------------------------------------------------------
/** An address that receives mail. A synthetic `.invalid` one is refused by the server. */
const StationEmailSchema = z.string().trim().min(3).max(254).regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'An email address');
export const StationEmailCodeRequestSchema = z.object({ email: StationEmailSchema }).openapi('StationEmailCodeRequest');
export const StationEmailCodeSentSchema = z
    .object({
    expiresInSeconds: z.number().int().positive(),
    resendAfterSeconds: z.number().int().positive(),
})
    .openapi('StationEmailCodeSent', { description: 'A code is on its way to the email address.' });
export const StationEmailSignInSchema = z
    .object({
    email: StationEmailSchema,
    code: OtpCodeSchema,
    /** As for the phone: required when this code creates the account; without it the code is kept. */
    consent: SignUpConsentSchema.optional(),
    /** As for the phone: optional, assigned by the server when blank or taken. */
    username: UsernameSchema.optional(),
})
    .openapi('StationEmailSignIn');
// -------------------------------------------------------------------------------------------------
// Finishing a just-created profile (create-account steps 4b–6, F-KIO-04, 2026-10-01)
// -------------------------------------------------------------------------------------------------
const PersonNameSchema = z.string().trim().min(1).max(60);
export const StationProfileCompletionSchema = z
    .object({
    /** `StationProfileCompletionGrant.token` from the sign-in that created the account. */
    token: z.string().min(32).max(128),
    firstName: PersonNameSchema.optional(),
    lastName: PersonNameSchema.optional(),
    /** Declared, not verified (`dobVerified` stays false). Read by the age check at the session start. */
    dateOfBirth: LocalDateSchema.optional(),
    /**
     * An email address to add — only to an account whose address is synthetic (made by phone). Nothing
     * changes yet: a code goes to it, and `POST /v1/station/sign-in/profile/email` adds it once verified.
     */
    email: StationEmailSchema.optional(),
    /**
     * A first password (owner's decision 2026-10-01, ADR-0030): the sign-up's own code, phone or email, is the
     * fresh code spec/07 §4.3 asks for, so the just-created player may set one here within the grant's window.
     * At least 8 characters, checked against breached-password lists; refused once the account has a password.
     */
    password: z.string().min(8).max(128).optional(),
})
    .strict()
    .openapi('StationProfileCompletion');
export const StationProfileEmailVerificationStartedSchema = z
    .object({
    /** Where the code went, masked. */
    destination: z.string(),
    expiresAt: IsoDateTimeSchema,
})
    .openapi('StationProfileEmailVerificationStarted');
export const StationProfileCompletedSchema = z
    .object({
    /** Present when an email address was asked for: where the code went (masked) and until when it works. Absent otherwise (never null — the station pipe omits nulls). */
    emailVerification: StationProfileEmailVerificationStartedSchema.optional(),
})
    .openapi('StationProfileCompleted', { description: 'The profile fields are saved.' });
export const StationProfileEmailVerificationSchema = z
    .object({ token: z.string().min(32).max(128), code: OtpCodeSchema })
    .openapi('StationProfileEmailVerification');
export const StationProfileEmailVerifiedSchema = z
    .object({ email: z.string().openapi({ description: 'The address added, masked.' }) })
    .openapi('StationProfileEmailVerified', { description: 'The email address is verified and on the account.' });
export const requestStationPhoneCodeRoute = createRoute({
    method: 'post',
    path: '/v1/station/sign-in/phone/code',
    operationId: 'requestStationPhoneCode',
    tags: ['identity'],
    summary: 'Send a sign-in code to a phone, from a kiosk',
    description: `${STATION_AUTHENTICATION} Sends a 6-digit code valid for 10 minutes by SMS or WhatsApp per ` +
        '`auth.otpChannel`. Numbers in premium-rate ranges are refused. A 503 means the code was not ' +
        'sent — never that it was.',
    request: {
        body: { content: { 'application/json': { schema: StationPhoneCodeRequestSchema } }, required: true },
    },
    responses: {
        202: jsonResponse(StationPhoneCodeSentSchema, 'The code has been handed to a provider.'),
        400: problemResponse('The request could not be understood, or the number cannot be used.', 'validation-failed', 'phone-number-refused'),
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        403: problemResponse(PHONE_SIGN_IN_OFF, 'phone-sign-in-off'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
        503: problemResponse('No provider could send the code; nothing was sent.', 'otp-delivery-unavailable'),
    },
});
export const signInAtStationWithPhoneRoute = createRoute({
    method: 'post',
    path: '/v1/station/sign-in/phone',
    operationId: 'signInAtStationWithPhone',
    tags: ['identity'],
    summary: 'Sign a player in at a kiosk with a phone code',
    description: `${STATION_AUTHENTICATION} Verifies the code and signs the player in, creating the account on ` +
        'first verification — only with the terms and privacy accepted in `consent`. A new number without ' +
        'them is refused with `consent-required` and keeps its code. `username` is optional even when this ' +
        'call creates the account: left out, or already taken, the server assigns a fresh gamer tag rather ' +
        'than refuse. Five wrong codes void the code; five failures in 15 minutes lock the account for 15 ' +
        'minutes.',
    request: {
        body: { content: { 'application/json': { schema: StationPhoneSignInSchema } }, required: true },
    },
    responses: {
        200: jsonResponse(StationSignInResultSchema, 'The player is signed in on this station.'),
        400: problemResponse('The code is wrong, expired or used up, or a new account was asked for without the terms and privacy accepted.', 'validation-failed', 'otp-invalid', 'otp-expired', 'consent-required'),
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        403: problemResponse(`${BANNED} Or: this venue does not create accounts at its PCs (\`kiosk.allowSignUp\` is off). Or: ${PHONE_SIGN_IN_OFF}`, 'player-banned', 'sign-up-disabled', 'phone-sign-in-off'),
        409: problemResponse('The terms or privacy version shown is not the one in force.', 'consent-version-outdated'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const signInAtStationWithPasswordRoute = createRoute({
    method: 'post',
    path: '/v1/station/sign-in/password',
    operationId: 'signInAtStationWithPassword',
    tags: ['identity'],
    summary: 'Sign a player in at a kiosk with an email address or username and a password',
    description: `${STATION_AUTHENTICATION} An unknown account, a wrong password and a malformed username all answer ` +
        '`invalid-credentials`, and a failure takes as long as any other. Five failures in 15 minutes lock ' +
        'the account for 15 minutes, here and everywhere else.',
    request: {
        body: { content: { 'application/json': { schema: StationPasswordSignInSchema } }, required: true },
    },
    responses: {
        200: jsonResponse(StationSignInResultSchema, 'The player is signed in on this station.'),
        400: commonProblemResponses[400],
        401: problemResponse('Not an authenticated station, or the credentials are wrong.', 'unauthenticated', 'invalid-credentials'),
        403: problemResponse(`The password is right but the email address has not been verified; or: ${BANNED}`, 'email-not-verified', 'player-banned'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
const PROFILE_WINDOW = 'Only for the account the sign-in just created, from the station it was created at, within the window its ' +
    '`profileCompletion` grant names (`profile-window-closed` otherwise).';
export const getStationSignInOptionsRoute = createRoute({
    method: 'get',
    path: '/v1/station/sign-in/options',
    operationId: 'getStationSignInOptions',
    tags: ['identity'],
    summary: 'What the kiosk’s sign-in panel offers at this station',
    description: `${STATION_AUTHENTICATION} Whether a new player can create an account here (\`kiosk.allowSignUp\`), by which ` +
        'paths — phone, email or both, depending on the providers this deployment has and on whether phone sign-in is ' +
        'on (`auth.phoneSignIn`) — whether a phone signs a player in at all, and whether a date of birth is required. ' +
        'Read when the sign-in panel opens; counts against no limit.',
    responses: {
        200: jsonResponse(StationSignInOptionsSchema, 'The sign-in panel’s options at this station.'),
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        500: commonProblemResponses[500],
    },
});
export const requestStationEmailCodeRoute = createRoute({
    method: 'post',
    path: '/v1/station/sign-in/email/code',
    operationId: 'requestStationEmailCode',
    tags: ['identity'],
    summary: 'Send a sign-in code to an email address, from a kiosk',
    description: `${STATION_AUTHENTICATION} Sends a 6-digit code valid for 10 minutes to the address, whether or not it is an ` +
        'account — the answer is the same either way. 3 codes per address per 10 minutes, 10 a day. A 503 means no ' +
        'email provider is configured and nothing was sent.',
    request: {
        body: { content: { 'application/json': { schema: StationEmailCodeRequestSchema } }, required: true },
    },
    responses: {
        202: jsonResponse(StationEmailCodeSentSchema, 'The code has been handed to the email provider.'),
        400: commonProblemResponses[400],
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
        503: problemResponse('No email provider is configured; nothing was sent.', 'otp-delivery-unavailable'),
    },
});
export const signInAtStationWithEmailRoute = createRoute({
    method: 'post',
    path: '/v1/station/sign-in/email',
    operationId: 'signInAtStationWithEmail',
    tags: ['identity'],
    summary: 'Sign a player in, or create their account, at a kiosk with an email code',
    description: `${STATION_AUTHENTICATION} Verifies the code. An address that is an account signs it in (and marks the address ` +
        'verified); a new address creates the account — only with `consent`, and only where `kiosk.allowSignUp` is on. ' +
        'A new address without `consent` is refused with `consent-required` and keeps its code. Five wrong codes void it; ' +
        'five failures in 15 minutes lock the account.',
    request: {
        body: { content: { 'application/json': { schema: StationEmailSignInSchema } }, required: true },
    },
    responses: {
        200: jsonResponse(StationSignInResultSchema, 'The player is signed in on this station.'),
        400: problemResponse('The code is wrong, expired or used up, or a new account was asked for without the terms and privacy accepted.', 'validation-failed', 'otp-invalid', 'otp-expired', 'consent-required'),
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        403: problemResponse(`${BANNED} Or: this venue does not create accounts at its PCs.`, 'player-banned', 'sign-up-disabled'),
        409: problemResponse('The terms or privacy version shown is not the one in force.', 'consent-version-outdated'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const completeStationProfileRoute = createRoute({
    method: 'post',
    path: '/v1/station/sign-in/profile',
    operationId: 'completeStationProfile',
    tags: ['identity'],
    summary: 'Finish a just-created account’s profile at the kiosk',
    description: `${STATION_AUTHENTICATION} ${PROFILE_WINDOW} Saves the first and last name, the declared date of birth and a ` +
        'first password (checked against breached-password lists; the sign-up code is the fresh code spec/07 §4.3 asks ' +
        'for); an `email` is not saved yet — a code goes to it. Never the phone, an existing address or two-factor. ' +
        'Ten calls per grant. Audited.',
    request: {
        body: { content: { 'application/json': { schema: StationProfileCompletionSchema } }, required: true },
    },
    responses: {
        200: jsonResponse(StationProfileCompletedSchema, 'Saved.'),
        400: commonProblemResponses[400],
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        403: problemResponse('The grant is unknown, expired, for another station, or the account already has an email or a password.', 'profile-window-closed'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
        503: problemResponse('No email provider is configured; the other fields were saved, no code was sent.', 'otp-delivery-unavailable'),
    },
});
export const verifyStationProfileEmailRoute = createRoute({
    method: 'post',
    path: '/v1/station/sign-in/profile/email',
    operationId: 'verifyStationProfileEmail',
    tags: ['identity'],
    summary: 'Add the email address a just-created account asked for, with its code',
    description: `${STATION_AUTHENTICATION} ${PROFILE_WINDOW} Spends the code sent by \`POST /v1/station/sign-in/profile\`; ` +
        'five wrong codes void it. The address is checked against every other account only once the code checks out. ' +
        'Then the player is emailed how to finish setting up their account (adding a password is done from the email, ' +
        'never at the PC). Audited.',
    request: {
        body: { content: { 'application/json': { schema: StationProfileEmailVerificationSchema } }, required: true },
    },
    responses: {
        200: jsonResponse(StationProfileEmailVerifiedSchema, 'The address is verified and on the account.'),
        400: problemResponse('The code is wrong, expired or used up.', 'validation-failed', 'otp-invalid', 'otp-expired'),
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        403: problemResponse('The grant is unknown, expired or for another station.', 'profile-window-closed'),
        409: problemResponse('The address belongs to another account.', 'contact-in-use'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
//# sourceMappingURL=station-sign-in.js.map